/* @flow */
import {useMemo} from 'react';
import {useQuery} from '@apollo/react-hooks';
import {connect} from 'react-redux';
import {addDays, subDays, format, parse as parseDate} from 'date-fns';

import {searchRequestQueryVariables} from '../../queries/searchRequestQuery';
import {flexibleDatesSearchRequestQuery} from '../../queries/flexibleDatesSearchRequestQuery';

import type {
    FlexibleDateRange,
    FlexibleDatesOptionProps,
    FlexibleDatesOptions
} from './FlexibleDates';
import type {Element} from 'react';
import type {State as ReduxState} from '../../reducers';

export type CallbackProps = FlexibleDatesOptionProps &
    FlexibleDateRange & {
        type: string,
        resultCountDifferenceForNewDates: number
    };

type StateProps = {
    abacus: $PropertyType<ReduxState, 'abacus'>,
    params: $PropertyType<$PropertyType<ReduxState, 'searchParams'>, 'params'>,
    system: $PropertyType<ReduxState, 'system'>
};

type OwnProps = {
    options: FlexibleDatesOptions,
    children: (callbackProps: Array<CallbackProps>, boolean) => Element<any>,
    loading: boolean,
    startDate: string,
    endDate: string,
    resultCount: number
};

type Props = OwnProps & StateProps;

const parseDateToString = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
};

const convertStringToDate = (dateStr: string): Date => {
    return parseDate(dateStr, 'yyyy-MM-dd', new Date());
};

const calculateRange = (
    type: 'add' | 'sub',
    days: number,
    startDateStr: string,
    endDateStr: string
): FlexibleDateRange => {
    const startDate = convertStringToDate(startDateStr);
    const endDate = convertStringToDate(endDateStr);
    const method = type === 'add' ? addDays : subDays;
    return {
        startDate: parseDateToString(method(startDate, days)),
        endDate: parseDateToString(method(endDate, days))
    };
};

const calculateNewDateOptions = (
    options: FlexibleDatesOptions,
    startDate,
    endDate
) => {
    return [
        ...options.add.map((opts) => ({
            ...opts,
            type: 'add',
            ...calculateRange('add', opts.days, startDate, endDate)
        })),
        ...options.sub.map((opts) => ({
            ...opts,
            type: 'sub',
            ...calculateRange('sub', opts.days, startDate, endDate)
        }))
    ].reduce((total, curr) => {
        total[curr.id] = curr;
        return total;
    }, {});
};

const makeVariables = (params, abacus, system, flexibleDateOptions) => {
    return Object.keys(flexibleDateOptions).reduce((total, key) => {
        const options = flexibleDateOptions[key];
        return {
            ...total,
            [key]: searchRequestQueryVariables(
                {
                    ...params,
                    arrival: options.startDate,
                    departure: options.endDate
                },
                abacus,
                system
            ).request
        };
    }, {});
};

const getResults = (flexibleDateOptions, resultCount, results) => {
    return [
        {
            ...flexibleDateOptions.OneWeekEarlier,
            resultCountDifferenceForNewDates: results
                ? results.oneWeekEarlierResults.resultCount - resultCount
                : null
        },
        {
            ...flexibleDateOptions.OneDayEarlier,
            resultCountDifferenceForNewDates: results
                ? results.oneDayEarlierResults.resultCount - resultCount
                : null
        },
        {
            ...flexibleDateOptions.OneDayLater,
            resultCountDifferenceForNewDates: results
                ? results.oneDayLaterResults.resultCount - resultCount
                : null
        },
        {
            ...flexibleDateOptions.OneWeekLater,
            resultCountDifferenceForNewDates: results
                ? results.oneWeekLaterResults.resultCount - resultCount
                : null
        }
    ];
};

export const FlexibleDatesProvider = (props: Props) => {
    const {
        children,
        loading,
        params,
        abacus,
        system,
        options,
        startDate,
        endDate,
        resultCount
    } = props;

    const flexibleDateOptions = useMemo(
        () => calculateNewDateOptions(options, startDate, endDate),
        [options, startDate, endDate]
    );

    const {data: flexibleDatesPropertyCounts, loading: isLoading} = useQuery(
        flexibleDatesSearchRequestQuery,
        {
            skip: loading,
            variables: makeVariables(
                params,
                abacus,
                system,
                flexibleDateOptions
            )
        }
    );

    return children(
        getResults(
            flexibleDateOptions,
            resultCount,
            flexibleDatesPropertyCounts
        ),
        isLoading
    );
};

const mapStateToProps = ({
    searchParams: {params},
    abacus,
    system
}: ReduxState): StateProps => {
    return {
        params,
        abacus,
        system
    };
};

export default connect(mapStateToProps)(FlexibleDatesProvider);
